// src/lib/analytics.ts
'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { EventParams } from '@/types/gtag';

export function useGoogleAnalytics(): void {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;
    if (!window.gtag) return;

    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag('event', 'page_view', { page_path: url });
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: EventParams = {}): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// Conversion funnel tracking functions
export function trackPlanSelection(planId: string, planName: string): void {
  trackEvent('plan_selected', {
    plan_id: planId,
    plan_name: planName,
    event_category: 'conversion_funnel',
    event_label: 'Plan Selection'
  });
}

export function trackEmailModalOpen(planId: string): void {
  trackEvent('signup_modal_opened', {
    plan_id: planId,
    event_category: 'conversion_funnel',
    event_label: 'Email Modal Opened'
  });
}

export function trackEmailSubmit(planId: string, email: string): void {
  trackEvent('email_submitted', {
    plan_id: planId,
    email_domain: email.split('@')[1],
    event_category: 'conversion_funnel',
    event_label: 'Email Submitted'
  });
}

export function trackCheckoutInitiated(planId: string, planName: string): void {
  trackEvent('begin_checkout', {
    plan_id: planId,
    plan_name: planName,
    event_category: 'conversion_funnel',
    event_label: 'Checkout Initiated'
  });
}

export function trackStockUploadRedirect(email: string): void {
  trackEvent('stock_upload_redirect', {
    email_domain: email.split('@')[1],
    event_category: 'conversion_funnel',
    event_label: 'Stock Partner Redirect'
  });
}

export function trackPricingCardView(planId: string): void {
  trackEvent('view_item', {
    plan_id: planId,
    event_category: 'engagement',
    event_label: 'Pricing Card Viewed'
  });
}

export function trackCTAClick(buttonText: string, location: string): void {
  trackEvent('cta_click', {
    button_text: buttonText,
    location: location,
    event_category: 'engagement',
    event_label: 'CTA Button Clicked'
  });
}
